import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Only the default Swedish bundle ships in the main JS payload. Other
// locales are dynamic-imported on demand (≈25 KB each), keeping initial
// download lean for first paint.
import sv from "./locales/sv.json";

const lazyLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import("./locales/en.json"),
  no: () => import("./locales/no.json"),
  da: () => import("./locales/da.json"),
  fi: () => import("./locales/fi.json"),
  is: () => import("./locales/is.json"),
};

export const SUPPORTED_LANGUAGES = [
  { code: "sv", label: "Svenska", flag: "🇸🇪" },
  { code: "no", label: "Norsk", flag: "🇳🇴" },
  { code: "da", label: "Dansk", flag: "🇩🇰" },
  { code: "fi", label: "Suomi", flag: "🇫🇮" },
  { code: "is", label: "Íslenska", flag: "🇮🇸" },
  { code: "en", label: "English", flag: "🇬🇧" },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "sv",
    lng: (() => {
      // Default first-visit language is Swedish. We only honor the browser's
      // language preference if the user has explicitly picked one before
      // (stored in localStorage). This avoids surprising Swedish-speaking
      // visitors with an English UI just because their browser is en-US.
      try {
        const stored = localStorage.getItem("tirevault_language");
        if (stored) return stored;
      } catch { /* ignore */ }
      return "sv";
    })(),
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    resources: {
      sv: { translation: sv },
    },
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    detection: {
      // Only localStorage — navigator detection is intentionally disabled so
      // first-time visitors always see Swedish until they explicitly switch.
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "tirevault_language",
    },
  });

// ----- Caching + fallback for lazy-loaded locales -----
// Strategy:
// 1. localStorage cache ("tirevault_locale_<code>_v<APP_VERSION>") gives instant
//    hydration on repeat visits — no network needed for previously seen languages.
// 2. Dynamic import with retry (1 retry after 600 ms) handles flaky chunks.
// 3. On total failure we fall back to the Swedish bundle so the UI never breaks.
// 4. In-flight de-dup prevents parallel imports for the same locale.

const LOCALE_CACHE_VERSION = "1"; // bump to invalidate cached bundles after copy edits
const cacheKey = (code: string) => `tirevault_locale_${code}_v${LOCALE_CACHE_VERSION}`;

const readCached = (code: string): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(cacheKey(code));
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const writeCached = (code: string, bundle: Record<string, unknown>) => {
  try {
    localStorage.setItem(cacheKey(code), JSON.stringify(bundle));
    // Best-effort: clear stale versions of this locale.
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        key.startsWith(`tirevault_locale_${code}_v`) &&
        key !== cacheKey(code)
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Quota exceeded or private mode — silently ignore, runtime still works.
  }
};

const inflight = new Map<string, Promise<void>>();

const importWithRetry = async (
  code: string,
  loader: () => Promise<{ default: Record<string, unknown> }>,
): Promise<Record<string, unknown>> => {
  try {
    const mod = await loader();
    return mod.default;
  } catch (err) {
    await new Promise((r) => setTimeout(r, 600));
    const mod = await loader();
    return mod.default;
  }
};

export const ensureLanguageLoaded = (lng: string): Promise<void> => {
  const code = (lng || "").split("-")[0];
  if (!code || code === "sv") return Promise.resolve();
  const loader = lazyLoaders[code];
  if (!loader) return Promise.resolve();
  if (i18n.hasResourceBundle(code, "translation")) return Promise.resolve();

  const existing = inflight.get(code);
  if (existing) return existing;

  // 1. Synchronous cache hit → instant hydration, no network.
  const cached = readCached(code);
  if (cached) {
    i18n.addResourceBundle(code, "translation", cached, true, true);
    // Refresh in background so updates ship without forcing a hard reload.
    const refresh = importWithRetry(code, loader)
      .then((bundle) => {
        i18n.addResourceBundle(code, "translation", bundle, true, true);
        writeCached(code, bundle);
      })
      .catch(() => {
        /* network down — keep the cached copy, no user impact */
      })
      .finally(() => inflight.delete(code));
    inflight.set(code, refresh);
    return Promise.resolve();
  }

  // 2. No cache → fetch with retry, fall back to Swedish on hard failure.
  const task = importWithRetry(code, loader)
    .then((bundle) => {
      i18n.addResourceBundle(code, "translation", bundle, true, true);
      writeCached(code, bundle);
    })
    .catch((err) => {
      console.warn("[i18n] locale load failed, staying on Swedish fallback", code, err);
      // Force i18next to resolve via fallback chain (sv) for this language.
      void i18n.changeLanguage("sv");
    })
    .finally(() => inflight.delete(code));
  inflight.set(code, task);
  return task;
};

/** Prefetch a locale without switching to it (used on hover/focus in the language switcher). */
export const prefetchLanguage = (lng: string) => {
  void ensureLanguageLoaded(lng);
};

void ensureLanguageLoaded(i18n.language || "sv");
i18n.on("languageChanged", (lng) => void ensureLanguageLoaded(lng));

// Keep <html lang> in sync
const updateHtmlLang = (lng: string) => {
  if (typeof document !== "undefined") document.documentElement.lang = lng;
};
updateHtmlLang(i18n.language || "sv");
i18n.on("languageChanged", updateHtmlLang);

export default i18n;
